<template>
  <v-container fluid>
    <v-data-table
      dense
      v-model="directiveAdminsSelected"
      :headers="headers"
      :items="items"
      :single-select="singleSelect"
      item-key="id"
      :loading="tableLoading"
      show-select
      class="elevation-0"
      checkbox-color="deep-purple"
      hide-default-footer
    >
      <template v-slot:item.school="{ item }">
        <div v-if="item.school" class="d-flex justify-start align-center">
          <img v-if="item.country"
            :src="`${flagsPath}${item.country.iso_code}.png`"
            alt="country flag"
            width="35vh"
            height="25vh"
            class="mr-2"
          />
          <span>{{item.school.name}}</span>
        </div>
      </template>
      <template v-slot:item.fullName="{ item }">
        <span class="font-weight-bold">
          {{ item.firstname }} {{ item.lastname }} {{ item.lastname2nd }}
        </span>
      </template>
      <template v-slot:item.register_date="{ item }">
        <span v-if="item.creationDate" class="font-weight-bold">
          {{ item.creationDate | moment('MM/D/YYYY') }}
        </span>
        <span v-else class="orange--text darken-2 font-weight-bold">Vacio</span>
      </template>
      <template v-slot:item.state="{ item }">
        <span v-if="item.active"
              class="green--text font-weight-bold">{{ $t('students.active')}}</span>
        <span v-else
              class="orange--text darken-2 font-weight-bold">{{ $t('students.inactive')}}</span>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-row justify="center" align="center" no-gutters>
          <v-col cols="auto">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="info"
                  @click="editDirectiveAdmin(item)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <span>Editar</span>
            </v-tooltip>
          </v-col>
          <v-col cols="auto">
            <v-btn icon :title="$t('students.impersonate')" @click="openImpersonateModal(item)">
              <v-icon>mdi-account-switch</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </template>
    </v-data-table>

    <!-- Impersonate App Selector Modal -->
    <general-modal-container v-if="isImpersonateModalOpen" @onClose="closeImpersonateModal">
      <impersonate-app-selector-modal
        :user-name="impersonateUserLabel"
        @onSelect="handleImpersonateAppSelect"
        @onCancel="closeImpersonateModal"
      />
    </general-modal-container>

    <!-- Toolbar para acciones multiples -->
    <v-toolbar
      v-if="directiveAdminsSelected.length > 0"
      dense
      flat
      color="grey lighten-3"
      class="mt-2"
    >
      <v-toolbar-title class="subtitle-2">
        {{ directiveAdminsSelected.length }}
        {{
          directiveAdminsSelected.length === 1
            ? 'directivo seleccionado'
            : 'directivos seleccionados'
        }}
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn
        color="red"
        outlined
        small
        @click="deleteSelected"
        :loading="deleteLoading"
        :disabled="deleteLoading"
      >
        <v-icon left small>mdi-delete</v-icon>
        Eliminar seleccionados
      </v-btn>
    </v-toolbar>

    <div class="text-center pt-2">
      <v-pagination
        @input="setValue($event)"
        v-model="page"
        :length="length"
        :total-visible="6"
        color="deep-purple lighten-3"
      ></v-pagination>
    </div>
  </v-container>
</template>

<script>
import { FLAGS_URL } from '../../constants/data';
import LoadingMixin from '../../mixins/loading';
import snackbarMixin from '../../mixins/snackbar';
import directiveAdminService from '../../services/directiveAdmin.service';
import impersonationService from '../../services/impersonation.service';
import GeneralModalContainer from '../core/modals/GeneralModalContainer.vue';
import ImpersonateAppSelectorModal from '../teachers/modals/ImpersonateAppSelectorModal.vue';
import {
  buildImpersonationLabel,
  buildImpersonationPayload,
  getImpersonationErrorMessage,
} from '../../utils/impersonation';

export default {
  components: {
    GeneralModalContainer,
    ImpersonateAppSelectorModal,
  },
  props: {
    items: { type: Array, default: () => ([]) },
    page: { type: Number, default: 1 },
    length: { type: Number, default: 1 },
    tableLoading: { type: Boolean, default: false },
  },
  computed: {
    impersonateUserLabel() {
      return this.userToImpersonate ? buildImpersonationLabel(this.userToImpersonate) : '';
    },
  },
  data: () => ({
    singleSelect: false,
    directiveAdminsSelected: [],
    flagsPath: `${FLAGS_URL}countries_flags/`,
    deleteLoading: false,
    headers: [
      {
        text: 'Nombre Completo',
        align: 'start',
        sortable: false,
        value: 'fullName',
      },
      { text: 'Email', value: 'email' },
      { text: 'DNI', value: 'dni' },
      { text: 'Sede', value: 'school' },
      { text: 'Fecha de registro', value: 'register_date' },
      { text: 'Estado', value: 'state' },
      {
        text: 'Acciones', value: 'actions', sortable: false, align: 'center',
      },
    ],
    isImpersonateModalOpen: false,
    userToImpersonate: null,
  }),
  mixins: [LoadingMixin, snackbarMixin],
  methods: {
    setValue(page) {
      this.$emit('input', page);
    },
    editDirectiveAdmin(directiveAdmin) {
      this.$emit('edit', directiveAdmin);
    },
    openImpersonateModal(user) {
      const payload = buildImpersonationPayload(user);
      if (!payload) {
        this.snackbarError(this.$t('students.action_error'));
        return;
      }
      this.userToImpersonate = user;
      this.isImpersonateModalOpen = true;
    },
    closeImpersonateModal() {
      this.isImpersonateModalOpen = false;
      this.userToImpersonate = null;
    },
    async handleImpersonateAppSelect(app) {
      const user = this.userToImpersonate;
      this.closeImpersonateModal();
      await this.impersonateUser(user, app === 'teachers');
    },
    async impersonateUser(user, useTeachersApp) {
      const payload = buildImpersonationPayload(user);
      if (!payload) {
        this.snackbarError(this.$t('students.action_error'));
        return;
      }
      this.loader();
      try {
        if (useTeachersApp) {
          const response = await impersonationService.impersonate(payload);
          const redirectURL = response?.content?.redirectURL;
          if (!redirectURL) throw new Error(String(response?.content ?? this.$t('students.action_error')));
          window.open(redirectURL, '_blank');
        } else {
          const response = await impersonationService.impersonateForDataSystem(payload);
          if (!response?.redirectURL) throw new Error(this.$t('students.action_error'));
          window.open(response.redirectURL, '_blank');
        }
        this.snackbarSuccess(`${this.$t('students.impersonate_opening')} ${buildImpersonationLabel(user)}`.trim());
      } catch (error) {
        this.snackbarError(getImpersonationErrorMessage(error, this.$t('students.action_error')));
      } finally {
        this.loader();
      }
    },
    async deleteSelected() {
      const count = this.directiveAdminsSelected.length;
      const confirmed = confirm(
        `¿Está seguro que desea eliminar ${count} ${count === 1 ? 'directivo' : 'directivos'}? Esta acción no se puede deshacer.`,
      );

      if (!confirmed) return;

      this.deleteLoading = true;
      try {
        const ids = this.directiveAdminsSelected.map((admin) => admin.id);
        await directiveAdminService.deleteMultipleDirectiveAdmins(ids);

        this.snackbarSuccess(
          `Se eliminaron ${ids.length} directivos correctamente`,
        );
        this.directiveAdminsSelected = [];
        this.$emit('refresh');
      } catch (error) {
        const errorMessage = this.getErrorMessage(error);
        this.snackbarError(errorMessage);
      } finally {
        this.deleteLoading = false;
      }
    },
    getErrorMessage(error) {
      return error.response?.data?.message
        || error.response?.data?.title
        || error.message
        || 'Error al eliminar directivos';
    },
  },
  watch: {
    directiveAdminsSelected(newVal) {
      this.$emit('selected', newVal);
    },
  },
};
</script>
